import { useEffect, useState } from 'react';

export type Queries<T> = Record<string, T>;

/**
 * Example:
 *
 * const columnCount = useMedia<number>({
 *     '(min-width: 768px)': 2,
 *     '(min-width: 1024px)': 4,
 * }, 1);
 */
const useMedia = <T>(queries: Queries<T>, fallbackValue: T) => {
    const mediaQueryLists = Object.keys(queries).map((q) =>
        window.matchMedia(q)
    );

    const getValue = () => {
        const index = mediaQueryLists.findIndex((mql) => mql.matches);
        if (index === -1) {
            return fallbackValue;
        }

        return Object.values(queries)[index]!;
    };

    const [value, setValue] = useState<T>(getValue);

    useEffect(() => {
        const handler = () => setValue(getValue);

        mediaQueryLists.forEach((mql) =>
            mql.addEventListener('change', handler)
        );

        return () =>
            mediaQueryLists.forEach((mql) =>
                mql.removeEventListener('change', handler)
            );
    }, []);

    return value;
};

export default useMedia;
